'use client';

import { Box, Typography, Divider, Stack, Chip, Paper } from '@mui/material';
import { useMediaBrowserV2 } from '@/app/features/shared/media-browser-v2/context/MediaBrowserV2Context';
import { renderMedia } from '@/app/features/shared/media-browser/media-renderer';
import MediaMetadataEditor from './MediaMetadataEditor';
import MediaUploadZone from './MediaUploadZone';
import MultiSelectView from './MultiSelectView';
import {
    Image as ImageIcon,
    Videocam,
    PictureAsPdf,
    CalendarToday,
    Storage,
    AspectRatio,
} from '@mui/icons-material';
import { useMemo } from 'react';
import { mediaRefType } from '@/app/features/shared/__to_be_integrated/types/general/media/media-helper';

export default function MediaDetailsPanel() {
    const { selectedMedia, selectedMediaList } = useMediaBrowserV2();
    const hasMultipleSelected = selectedMediaList.length > 1;

    return (
        <Box
            className="flex h-full w-[360px] min-w-[360px] max-w-[360px] flex-col overflow-hidden"
            sx={{
                borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
            }}
        >
            {/* Upload Zone - always visible at top */}
            <Box className="shrink-0 p-4">
                <MediaUploadZone />
            </Box>

            <Divider />

            {/* Multi-Select View */}
            {hasMultipleSelected && (
                <>
                    <MultiSelectView />
                    <Divider />
                </>
            )}

            {/* Selected Media Preview & Details */}
            {selectedMedia && !hasMultipleSelected ? (
                <Box
                    className="flex flex-1 flex-col overflow-auto"
                >
                    {/* Preview */}
                    <Box className="shrink-0 p-4">
                        <Paper
                            className="relative w-full overflow-hidden aspect-video"
                            sx={{
                                backgroundColor: 'background.default',
                            }}
                        >
                            {renderMedia(selectedMedia)}
                        </Paper>
                    </Box>

                    <Divider />

                    {/* Metadata Editor */}
                    <Box className="shrink-0">
                        <MediaMetadataEditor />
                    </Box>

                    <Divider />

                    {/* Additional Info */}
                    <Box className="shrink-0 p-4">
                        <MediaInfo media={selectedMedia} />
                    </Box>
                </Box>
            ) : (
                <Box
                    className="flex flex-1 items-center justify-center p-6"
                >
                    <Typography variant="body2" color="text.disabled" align="center">
                        Kein Medium ausgewählt
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

function MediaInfo({ media }: { media: any }) {
    const isPending = 'uploadStatus' in media;
    const mediaType = useMemo(() => mediaRefType(media), [media]);

    const getMediaTypeIcon = () => {
        switch (mediaType) {
            case 'image':
                return <ImageIcon fontSize="small" />;
            case 'video':
                return <Videocam fontSize="small" />;
            case 'pdf':
                return <PictureAsPdf fontSize="small" />;
            default:
                return null;
        }
    };

    const getMediaTypeLabel = () => {
        switch (mediaType) {
            case 'image':
                return 'Bild';
            case 'video':
                return 'Video';
            case 'pdf':
                return 'PDF Dokument';
            default:
                return 'Unbekannt';
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (isPending) {
        return (
            <Stack spacing={2}>
                <Typography variant="subtitle2" fontWeight={600}>
                    Datei-Informationen
                </Typography>
                <InfoRow
                    icon={getMediaTypeIcon()}
                    label="Typ"
                    value={getMediaTypeLabel()}
                />
                {media.file && (
                    <InfoRow
                        icon={<Storage fontSize="small" />}
                        label="Größe"
                        value={formatFileSize(media.file.size)}
                    />
                )}
                <Chip
                    label="Noch nicht hochgeladen"
                    color="warning"
                    size="small"
                    sx={{ alignSelf: 'flex-start' }}
                />
            </Stack>
        );
    }

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle2" fontWeight={600}>
                Datei-Informationen
            </Typography>

            <InfoRow
                icon={getMediaTypeIcon()}
                label="Typ"
                value={getMediaTypeLabel()}
            />

            {media.mime && (
                <InfoRow
                    label="MIME-Type"
                    value={media.mime}
                />
            )}

            {media.size && (
                <InfoRow
                    icon={<Storage fontSize="small" />}
                    label="Dateigröße"
                    value={formatFileSize(media.size * 1024)}
                />
            )}

            {media.width && media.height && (
                <InfoRow
                    icon={<AspectRatio fontSize="small" />}
                    label="Dimensionen"
                    value={`${media.width} × ${media.height} px`}
                />
            )}

            {media.createdAt && (
                <InfoRow
                    icon={<CalendarToday fontSize="small" />}
                    label="Hochgeladen"
                    value={formatDate(media.createdAt)}
                />
            )}

            {media.ext && (
                <InfoRow
                    label="Format"
                    value={media.ext.toUpperCase().replace('.', '')}
                />
            )}

            {media.documentId && (
                <InfoRow
                    label="ID"
                    value={media.documentId}
                    valueProps={{ sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                />
            )}
        </Stack>
    );
}

function InfoRow({
    icon,
    label,
    value,
    valueProps,
}: {
    icon?: React.ReactNode;
    label: string;
    value: string;
    valueProps?: any;
}) {
    return (
        <Box className="flex items-start gap-1">
            {icon && (
                <Box className="pt-0.5" sx={{ color: 'text.secondary' }}>
                    {icon}
                </Box>
            )}
            <Box className="flex-1">
                <Typography variant="caption" color="text.secondary" display="block">
                    {label}
                </Typography>
                <Typography variant="body2" {...valueProps}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
}
